/**
 * Plan catalogue for the paywall, mirroring the marketing pricing page
 * (`tessera-marketing-website` → `src/components/marketing/pricing-cards.tsx`).
 *
 * Prices here are display copy. Stripe Checkout is the source of truth for the
 * amount charged. Interval tabs map to POST /api/v1/billing/checkout `{ plan, interval }`.
 * The launch offer is shown on Monthly (banner + intro prices), not as a separate tab —
 * same as the marketing site. Checkout uses `3month` while the offer is live.
 */

import type { BillingCheckoutInterval, BillingPlan } from "../types/billing";

export type BillingInterval = "monthly" | "launch" | "yearly";

/** Visible tabs — Monthly / Yearly only (matches marketing pricing page). */
export const BILLING_INTERVAL_TABS: readonly {
  id: Exclude<BillingInterval, "launch">;
  label: string;
}[] = [
  { id: "monthly", label: "Monthly" },
  { id: "yearly", label: "Yearly" },
];

export const DEFAULT_BILLING_INTERVAL: BillingInterval = "monthly";

export function checkoutIntervalFromUi(interval: BillingInterval): BillingCheckoutInterval {
  if (interval === "yearly") return "year";
  if (interval === "launch") return "3month";
  return "month";
}

/** Monthly tab headlines intro pricing while the paywall shows the launch offer. */
export function paywallShowsIntroMonthlyPricing(
  interval: BillingInterval,
  launchOfferLive: boolean
): boolean {
  return launchOfferLive && (interval === "monthly" || interval === "launch");
}

export function checkoutIntervalForPaywallTab(
  interval: BillingInterval,
  useIntroOfferCheckout: boolean
): BillingCheckoutInterval {
  if (useIntroOfferCheckout && paywallShowsIntroMonthlyPricing(interval, true)) {
    return "3month";
  }
  return checkoutIntervalFromUi(interval);
}

/** Always Monthly / Yearly — the 3-month offer is not a separate tab. */
export function billingIntervalTabs(
  _launchOfferLive?: boolean
): readonly {
  id: Exclude<BillingInterval, "launch">;
  label: string;
}[] {
  return BILLING_INTERVAL_TABS;
}

export function defaultBillingInterval(_launchOfferLive?: boolean): BillingInterval {
  return DEFAULT_BILLING_INTERVAL;
}

export const PAYWALL_HEADER = {
  titleLead: "Choose your",
  titleAccent: "plan",
  description:
    "Two plans: standard usage for everyday review, or higher limits when you need more headroom.",
} as const;

export const LAUNCH_OFFER = {
  badge: "Exclusive launch offer",
  titleLead: "3 months for as little as",
  titleAccent: "$45",
  description:
    "Join Tessera at our lowest membership cost. $15 per month for Standard and $30 per month for Plus for the first 3 months. Full platform access. Limited time. After that, membership continues at the regular monthly rate.",
} as const;

export interface SubscriptionPlanPrice {
  amount: string;
  /** Omitted for `yearly`, where the card derives a per-month figure. */
  period?: string;
  launchOffer?: string;
}

export interface SubscriptionPlanDefinition {
  id: string;
  name: string;
  description: string;
  /** Right-hand outline badge on the card header. */
  usageLabel: string;
  featuresHeading: string;
  features: readonly string[];
  prices: Record<BillingInterval, SubscriptionPlanPrice>;
  cta: string;
  /** Tinted card + gradient CTA. */
  highlighted: boolean;
  checkoutPlan: BillingPlan;
}

export const SUBSCRIPTION_PLANS: readonly SubscriptionPlanDefinition[] = [
  {
    id: "standard",
    name: "Standard",
    description:
      "Full Tessera access with standard usage limits, built for traders establishing a repeatable review process.",
    usageLabel: "Standard usage",
    featuresHeading: "What's included:",
    features: [
      "Trade log",
      "Statistics",
      "Reports (10 per week)",
      "Simulations (up to 5 attached per report)",
      "Strategies & workspace",
      "University",
    ],
    prices: {
      monthly: { amount: "$19", period: "per month" },
      yearly: { amount: "$180" },
      launch: {
        amount: "$45",
        launchOffer: "Exclusive launch offer",
      },
    },
    cta: "Get started",
    highlighted: false,
    checkoutPlan: "standard",
  },
  {
    id: "plus",
    name: "Plus",
    description:
      "Everything in Standard with more reports each week and more simulations you can keep attached to each report.",
    usageLabel: "Higher usage",
    featuresHeading: "Everything in Standard, plus:",
    features: [
      "Reports (40 per week)",
      "Simulations (up to 16 attached per report)",
      "Priority access to new features and updates",
    ],
    prices: {
      monthly: { amount: "$39", period: "per month" },
      yearly: { amount: "$372" },
      launch: {
        amount: "$90",
        launchOffer: "Exclusive launch offer",
      },
    },
    cta: "Get started",
    highlighted: true,
    checkoutPlan: "plus",
  },
];

/** Yearly cards headline the per-month equivalent of the annual amount. */
export function monthlyFromAnnual(annualAmount: string): string {
  const annual = Number.parseFloat(annualAmount.replace(/[^\d.]/g, ""));
  if (!Number.isFinite(annual)) return annualAmount;

  const monthly = annual / 12;
  if (Number.isInteger(monthly)) return `$${monthly}`;
  return `$${(Math.round(monthly * 100) / 100).toFixed(2)}`;
}

/** 3-month offer cards headline the per-month equivalent of the 3-month total. */
export function monthlyFromThreeMonth(totalAmount: string): string {
  const total = Number.parseFloat(totalAmount.replace(/[^\d.]/g, ""));
  if (!Number.isFinite(total)) return totalAmount;

  const monthly = total / 3;
  if (Number.isInteger(monthly)) return `$${monthly}`;
  return `$${(Math.round(monthly * 100) / 100).toFixed(2)}`;
}
